import React, { useState, useCallback } from 'react';
import type { TripStatusQueryRow } from '../../../types/tripStatusApiUsage';
import { formatNumber } from '../utils/formatNumber';
import TableHeader from './TableHeader';
import SortableColumnHeader from './SortableColumnHeader';
import type { SortDirection } from './SortableColumnHeader';
import tableStyles from '../../../components/table/styles.module.css';
import styles from './tripStatusQueriesTable.module.css';

interface TripStatusQueriesTableProps {
  data: TripStatusQueryRow[];
  onExport: () => void;
}

/**
 * Horizontal table: first column = "Queries" label, remaining columns = one
 * per time period (month/week/day). Only one data row, so sort icons are shown
 * per Figma but the period order stays fixed regardless of sort clicks.
 */
const TripStatusQueriesTable: React.FC<TripStatusQueriesTableProps> = ({
  data,
  onExport,
}) => {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const handleSort = useCallback((key: string) => {
    // Icon-only toggle: single-row table so data order does not change.
    setSortKey(prevKey => {
      if (prevKey === key) {
        setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortDirection('asc');
      }
      return key;
    });
  }, []);

  return (
    <div className={styles.wrapper}>
      <TableHeader title="TripStatus queries" onExport={onExport} />
      <div className={styles.scrollArea}>
        <table className={`${tableStyles.table} ${tableStyles.tableStriped}`}>
          <thead className={tableStyles.tableHead}>
            <tr>
              <SortableColumnHeader
                label="Month"
                columnKey="month"
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSort={handleSort}
                className={styles.firstCol}
              />
              {data.map(row => (
                <SortableColumnHeader
                  key={row.month}
                  label={row.month}
                  columnKey={row.month}
                  sortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
              ))}
            </tr>
          </thead>
          <tbody className={tableStyles.tbody}>
            <tr>
              <td className={styles.firstCol}>Queries</td>
              {data.map(row => (
                <td key={row.month}>{formatNumber(row.queries)}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TripStatusQueriesTable;
